import { Component, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatStepper } from '@angular/material/stepper';
import { SidebarNavMenuItem } from '../../../decorators/sidebar-nav-menu-item';
import { TeamEntity } from '../../../models/entities/team-entity';
import { UserEntity } from '../../../models/entities/user-entity';
import { TeamEntityService } from '../../../services/settings/team-entity.service';
import { Log, IShortLogger } from '../../../services/logger.service';
import { DEFAULT_PAGINATOR_PAGE_SIZE, PAGINATOR_PAGE_SIZE_OPTIONS } from '../../../shared/pagination-options';

@SidebarNavMenuItem({
  route: '/teams',
  faIcon: 'groups',
  name: 'Teams',
  order: 2300,
  businessProcessesName: 'Teams'
})
@Component({
  selector: 'app-teams',
  templateUrl: './teams.component.html',
  styleUrls: ['./teams.component.css']
})
export class TeamsComponent implements OnInit, AfterViewInit {
  private readonly logger: IShortLogger<TeamsComponent> = Log.create(TeamsComponent);

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild('deleteStepper') deleteStepper!: MatStepper;

  viewMode: 'detail' | 'card' = 'detail';
  isLoading = false;
  searchTerm = '';

  dataSource = new MatTableDataSource<TeamEntity>([]);
  readonly displayedColumns = ['name', 'userCount', 'status', 'actions'];
  readonly pageSizeOptions = PAGINATOR_PAGE_SIZE_OPTIONS;
  readonly pageSize = DEFAULT_PAGINATOR_PAGE_SIZE;

  teamUsers: UserEntity[] = [];

  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showDetailModal = false;
  selectedTeam: TeamEntity | null = null;

  teamForm!: FormGroup;

  // Delete workflow (flattened state)
  deleteStep: 'select-users' | 'select-target' | 'confirm' = 'select-users';
  reassignSelectedUserIds: string[] = [];
  reassignTargetTeamId = '';
  isReassigning = false;

  constructor(
    private fb: FormBuilder,
    private teamService: TeamEntityService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadTeams();
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  initializeForm(): void {
    this.teamForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      isDeletable: [true]
    });
  }

  private notify(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): void {
    const panelClass: Record<string, string> = {
      success: 'snack-success',
      error: 'snack-error',
      warning: 'snack-warning',
      info: 'snack-info'
    };
    this.snackBar.open(message, 'Close', { duration: 3000, panelClass: panelClass[type] });
  }

  loadTeams(): void {
    this.isLoading = true;
    this.teamService.getAllTeams().subscribe({
      next: (teams: TeamEntity[]) => {
        this.dataSource.data = teams.map(t => new TeamEntity(t));
        this.logger.i('Loaded teams:', this.dataSource.data.length);
        this.isLoading = false;
      },
      error: (err) => {
        this.logger.e('Error loading teams:', err);
        this.notify('Failed to load teams', 'error');
        this.isLoading = false;
      }
    });
  }

  loadTeamUsers(teamId: string): Promise<UserEntity[]> {
    return new Promise((resolve, reject) => {
      this.teamService.getUsersByTeam(teamId).subscribe({
        next: (users: UserEntity[]) => {
          this.logger.i('Loaded users for team:', teamId, 'count:', users.length);
          resolve(users);
        },
        error: (err) => {
          this.logger.e('Error loading team users:', err);
          reject(err);
        }
      });
    });
  }

  onViewChange(value: string): void {
    this.viewMode = value as 'detail' | 'card';
  }

  onSearchChange(): void {
    this.dataSource.filter = this.searchTerm.trim().toLowerCase();
  }

  openAddModal(): void {
    this.selectedTeam = null;
    this.teamForm.reset({ name: '', isDeletable: true });
    this.showAddModal = true;
  }

  closeAddModal(): void {
    this.showAddModal = false;
    this.teamForm.reset({ name: '', isDeletable: true });
  }

  async saveNewTeam(): Promise<void> {
    if (this.teamForm.invalid) {
      this.notify('Please fill all required fields', 'warning');
      return;
    }

    const teamData = new TeamEntity({
      name: this.teamForm.value.name,
      isDeletable: this.teamForm.value.isDeletable ?? false
    });

    try {
      this.isLoading = true;
      const created = await this.teamService.createTeam(teamData);
      this.logger.i('Team created:', created);
      this.notify(`Team '${created.name}' created successfully`, 'success');
      this.closeAddModal();
      this.loadTeams();
    } catch (err: any) {
      this.logger.e('Error creating team:', err);
      const message = err?.error?.message || err?.message || 'Failed to create team';
      this.notify(message, 'error');
    } finally {
      this.isLoading = false;
    }
  }

  openEditModal(team: TeamEntity): void {
    this.selectedTeam = team;
    this.teamForm.patchValue({
      name: team.name,
      isDeletable: team.isDeletable
    });

    if (team.isSystemTeam()) {
      this.teamForm.get('name')?.disable();
      this.teamForm.get('isDeletable')?.disable();
    } else {
      this.teamForm.get('name')?.enable();
      this.teamForm.get('isDeletable')?.enable();
    }

    this.showEditModal = true;
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.selectedTeam = null;
    this.teamForm.reset({ name: '', isDeletable: true });
    this.teamForm.get('name')?.enable();
    this.teamForm.get('isDeletable')?.enable();
  }

  updateTeam(): void {
    if (this.teamForm.invalid || !this.selectedTeam) {
      this.notify('Please fill all required fields', 'warning');
      return;
    }

    const updatedTeam = new TeamEntity({
      ...this.selectedTeam,
      name: this.teamForm.value.name,
      isDeletable: this.teamForm.value.isDeletable
    });

    this.isLoading = true;
    this.teamService.updateTeam(updatedTeam).subscribe({
      next: (result) => {
        this.logger.i('Team updated:', result);
        this.notify(`Team '${updatedTeam.name}' updated successfully`, 'success');
        this.closeEditModal();
        this.loadTeams();
      },
      error: (err) => {
        this.logger.e('Error updating team:', err);
        const message = err?.error || 'Failed to update team';
        this.notify(message, 'error');
        this.isLoading = false;
      }
    });
  }

  async openDetailModal(team: TeamEntity): Promise<void> {
    this.selectedTeam = team;
    try {
      this.teamUsers = await this.loadTeamUsers(team.id);
      this.showDetailModal = true;
    } catch (err) {
      this.notify('Failed to load team users', 'error');
    }
  }

  closeDetailModal(): void {
    this.showDetailModal = false;
    this.selectedTeam = null;
    this.teamUsers = [];
  }

  async openDeleteModal(team: TeamEntity): Promise<void> {
    if (team.isSystemTeam()) {
      this.notify(`Cannot delete protected team '${team.name}'`, 'error');
      return;
    }

    try {
      this.teamUsers = await this.loadTeamUsers(team.id);
      this.selectedTeam = team;
      this.reassignSelectedUserIds = [];
      this.reassignTargetTeamId = '';
      this.deleteStep = this.teamUsers.length === 0 ? 'confirm' : 'select-users';
      this.showDeleteModal = true;
      // Reset stepper to correct initial step after render
      setTimeout(() => {
        if (this.deleteStepper) {
          this.deleteStepper.reset();
          if (this.teamUsers.length === 0) {
            this.deleteStepper.next();
            this.deleteStepper.next();
          }
        }
      }, 0);
    } catch (err) {
      this.notify('Failed to check team users', 'error');
    }
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.selectedTeam = null;
    this.teamUsers = [];
    this.reassignSelectedUserIds = [];
    this.reassignTargetTeamId = '';
    this.deleteStep = 'select-users';
    setTimeout(() => this.deleteStepper?.reset(), 0);
  }

  toggleUserSelection(userId: string): void {
    const idx = this.reassignSelectedUserIds.indexOf(userId);
    if (idx === -1) {
      this.reassignSelectedUserIds.push(userId);
    } else {
      this.reassignSelectedUserIds.splice(idx, 1);
    }
  }

  isUserSelected(userId: string): boolean {
    return this.reassignSelectedUserIds.includes(userId);
  }

  selectAllUsers(): void {
    this.reassignSelectedUserIds = this.teamUsers.map(u => u.id);
  }

  clearAllUsers(): void {
    this.reassignSelectedUserIds = [];
  }

  get canProceedToTargetSelection(): boolean {
    return this.reassignSelectedUserIds.length > 0;
  }

  proceedToTargetSelection(): void {
    if (!this.canProceedToTargetSelection) {
      this.notify('Please select at least one user to reassign', 'warning');
      return;
    }
    this.deleteStep = 'select-target';
    setTimeout(() => this.deleteStepper?.next(), 0);
  }

  backToUserSelection(): void {
    this.deleteStep = 'select-users';
    setTimeout(() => this.deleteStepper?.previous(), 0);
  }

  get canConfirmReassignment(): boolean {
    return this.reassignTargetTeamId !== '';
  }

  proceedToConfirmation(): void {
    if (!this.canConfirmReassignment) {
      this.notify('Please select a target team', 'warning');
      return;
    }
    this.deleteStep = 'confirm';
    setTimeout(() => this.deleteStepper?.next(), 0);
  }

  backToTargetSelection(): void {
    this.deleteStep = 'select-target';
    setTimeout(() => this.deleteStepper?.previous(), 0);
  }

  getTargetTeamName(): string {
    return this.dataSource.data.find(t => t.id === this.reassignTargetTeamId)?.name || '';
  }

  getAvailableTargetTeams(): TeamEntity[] {
    return this.dataSource.data.filter(t => t.id !== this.selectedTeam?.id);
  }

  async executeReassignment(): Promise<void> {
    if (!this.selectedTeam || !this.canConfirmReassignment) {
      return;
    }

    this.isReassigning = true;
    try {
      const result = await new Promise((resolve, reject) => {
        this.teamService.reassignUsers(
          this.selectedTeam!.id,
          this.reassignSelectedUserIds,
          this.reassignTargetTeamId
        ).subscribe({ next: resolve, error: reject });
      });

      this.logger.i('Reassignment result:', result);
      this.notify(`Successfully reassigned ${this.reassignSelectedUserIds.length} users`, 'success');

      this.teamUsers = await this.loadTeamUsers(this.selectedTeam.id);

      if (this.teamUsers.length === 0) {
        this.deleteStep = 'confirm';
        setTimeout(() => {
          this.deleteStepper?.reset();
          this.deleteStepper?.next();
          this.deleteStepper?.next();
        }, 0);
      } else {
        this.reassignSelectedUserIds = [];
        this.reassignTargetTeamId = '';
        this.deleteStep = 'select-users';
        setTimeout(() => this.deleteStepper?.reset(), 0);
      }
    } catch (err: any) {
      this.logger.e('Error reassigning users:', err);
      const message = err?.error?.message || 'Failed to reassign users';
      this.notify(message, 'error');
    } finally {
      this.isReassigning = false;
    }
  }

  async confirmDelete(): Promise<void> {
    if (!this.selectedTeam) {
      return;
    }

    if (this.teamUsers.length > 0) {
      this.notify('Cannot delete team while users are still assigned', 'error');
      return;
    }

    this.isLoading = true;
    try {
      const response = await new Promise<any>((resolve, reject) => {
        this.teamService.deleteTeam(this.selectedTeam!.id).subscribe({ next: resolve, error: reject });
      });

      this.logger.i('Team deleted:', this.selectedTeam.name);
      const successMessage = response?.message || `Team '${this.selectedTeam.name}' deleted successfully`;
      this.notify(successMessage, 'success');
      this.closeDeleteModal();
      this.loadTeams();
    } catch (err: any) {
      this.logger.e('Error deleting team:', err);
      const message = err?.error?.message || err?.error || 'Failed to delete team';
      this.notify(message, 'error');
    } finally {
      this.isLoading = false;
    }
  }
}
